/**
 * City District Sub-Generators
 * Each district type has a distinct spatial algorithm producing a unique feel:
 * - residential: grid streets → blocks → subdivide → buildings (classic)
 * - market: central open plaza + stall entities + surrounding small buildings
 * - noble: wide streets, large plots, occasional courtyards
 * - slums: cellular automata → organic alleys, wall pockets as buildings
 */

import type { GridMap, Room, CityDistrictType } from '../types';
import { setTerrain, getCell, inBounds, carveRoom, coordKey } from '../grid';
import { SeededRandom } from '../random';
import type { CityBuilding } from './city-catalog';

// ============================================================================
// TYPES
// ============================================================================

export interface DistrictRect {
  x: number;
  y: number;
  width: number;
  height: number;
  type: CityDistrictType;
  id: number;
}

export interface DistrictResult {
  rooms: Room[];
  buildings: CityBuilding[];
}

// ============================================================================
// BSP PARTITIONING INTO DISTRICTS
// ============================================================================

interface BSPDistrictNode {
  x: number;
  y: number;
  width: number;
  height: number;
  left?: BSPDistrictNode;
  right?: BSPDistrictNode;
  splitAxis?: 'h' | 'v';
  splitPos?: number;
}

const MIN_DISTRICT_SIZE = 15;

export function partitionIntoDistricts(
  x: number, y: number, width: number, height: number,
  count: number, rng: SeededRandom,
): BSPDistrictNode {
  const root: BSPDistrictNode = { x, y, width, height };
  splitDistrictNode(root, count - 1, rng);
  return root;
}

function splitDistrictNode(node: BSPDistrictNode, splitsRemaining: number, rng: SeededRandom): void {
  if (splitsRemaining <= 0) return;
  if (node.width < MIN_DISTRICT_SIZE * 2 && node.height < MIN_DISTRICT_SIZE * 2) return;

  // Split along longer axis
  let splitH: boolean;
  if (node.width < MIN_DISTRICT_SIZE * 2) splitH = true;
  else if (node.height < MIN_DISTRICT_SIZE * 2) splitH = false;
  else splitH = node.height >= node.width;

  if (splitH) {
    const min = Math.max(MIN_DISTRICT_SIZE, Math.floor(node.height * 0.3));
    const max = Math.min(node.height - MIN_DISTRICT_SIZE, Math.floor(node.height * 0.7));
    if (min > max) return;
    const split = rng.int(min, max);
    node.splitAxis = 'h';
    node.splitPos = node.y + split;
    node.left = { x: node.x, y: node.y, width: node.width, height: split };
    node.right = { x: node.x, y: node.y + split, width: node.width, height: node.height - split };
  } else {
    const min = Math.max(MIN_DISTRICT_SIZE, Math.floor(node.width * 0.3));
    const max = Math.min(node.width - MIN_DISTRICT_SIZE, Math.floor(node.width * 0.7));
    if (min > max) return;
    const split = rng.int(min, max);
    node.splitAxis = 'v';
    node.splitPos = node.x + split;
    node.left = { x: node.x, y: node.y, width: split, height: node.height };
    node.right = { x: node.x + split, y: node.y, width: node.width - split, height: node.height };
  }

  // Distribute remaining splits between children
  const leftSplits = Math.floor(splitsRemaining / 2);
  const rightSplits = splitsRemaining - 1 - leftSplits;
  if (leftSplits > 0) splitDistrictNode(node.left!, leftSplits, rng);
  if (rightSplits > 0) splitDistrictNode(node.right!, rightSplits, rng);
}

/** Collect leaf nodes as district rectangles. */
export function collectLeaves(node: BSPDistrictNode): Array<{ x: number; y: number; width: number; height: number }> {
  if (!node.left && !node.right) {
    return [{ x: node.x, y: node.y, width: node.width, height: node.height }];
  }
  const result: Array<{ x: number; y: number; width: number; height: number }> = [];
  if (node.left) result.push(...collectLeaves(node.left));
  if (node.right) result.push(...collectLeaves(node.right));
  return result;
}

/** Carve avenues along BSP split lines. */
export function carveAvenues(map: GridMap, node: BSPDistrictNode, avenueWidth: number): void {
  if (!node.splitPos) return;

  if (node.splitAxis === 'h') {
    const y0 = node.splitPos - Math.floor(avenueWidth / 2);
    for (let dy = 0; dy < avenueWidth; dy++) {
      const y = y0 + dy;
      if (y >= 0 && y < map.height) {
        for (let x = node.x; x < node.x + node.width && x < map.width; x++) {
          setTerrain(map, x, y, 'floor');
        }
      }
    }
  } else {
    const x0 = node.splitPos - Math.floor(avenueWidth / 2);
    for (let dx = 0; dx < avenueWidth; dx++) {
      const x = x0 + dx;
      if (x >= 0 && x < map.width) {
        for (let y = node.y; y < node.y + node.height && y < map.height; y++) {
          setTerrain(map, x, y, 'floor');
        }
      }
    }
  }

  if (node.left) carveAvenues(map, node.left, avenueWidth);
  if (node.right) carveAvenues(map, node.right, avenueWidth);
}

// ============================================================================
// RESIDENTIAL DISTRICT — Grid streets → blocks → buildings
// ============================================================================

export function generateResidential(
  map: GridMap, rect: DistrictRect, rng: SeededRandom, roomIdStart: number,
): DistrictResult {
  const streetWidth = 1;
  const blockSize = 6 + rng.int(0, 2);
  const rooms: Room[] = [];
  const buildings: CityBuilding[] = [];
  let roomId = roomIdStart;

  // Inset by 1 to leave wall border
  const x0 = rect.x + 1;
  const y0 = rect.y + 1;
  const x1 = rect.x + rect.width - 1;
  const y1 = rect.y + rect.height - 1;

  // Carve streets
  const hStreets: number[] = [];
  const vStreets: number[] = [];

  for (let y = y0; y < y1; y += blockSize + streetWidth) {
    for (let dy = 0; dy < streetWidth; dy++) {
      const sy = y + dy;
      if (sy < y1) {
        for (let x = x0; x < x1; x++) setTerrain(map, x, sy, 'floor');
        hStreets.push(sy);
      }
    }
  }

  for (let x = x0; x < x1; x += blockSize + streetWidth) {
    for (let dx = 0; dx < streetWidth; dx++) {
      const sx = x + dx;
      if (sx < x1) {
        for (let y = y0; y < y1; y++) setTerrain(map, sx, y, 'floor');
        vStreets.push(sx);
      }
    }
  }

  // Identify wall blocks and subdivide into buildings
  const blocks = identifyWallBlocks(map, x0, y0, x1 - x0, y1 - y0);
  for (const block of blocks) {
    const innerW = block.width - 2;
    const innerH = block.height - 2;
    if (innerW < 2 || innerH < 2) continue;

    if (innerW >= 8 && innerH >= 8) {
      const midW = Math.floor(innerW / 2);
      const midH = Math.floor(innerH / 2);
      rooms.push({ x: block.x + 1, y: block.y + 1, width: midW - 1, height: midH - 1, id: roomId++ });
      rooms.push({ x: block.x + 1 + midW, y: block.y + 1, width: innerW - midW - 1, height: midH - 1, id: roomId++ });
      rooms.push({ x: block.x + 1, y: block.y + 1 + midH, width: midW - 1, height: innerH - midH - 1, id: roomId++ });
      rooms.push({ x: block.x + 1 + midW, y: block.y + 1 + midH, width: innerW - midW - 1, height: innerH - midH - 1, id: roomId++ });
    } else if (innerW >= 6 || innerH >= 6) {
      if (innerW > innerH) {
        const split = Math.floor(innerW / 2);
        rooms.push({ x: block.x + 1, y: block.y + 1, width: split - 1, height: innerH, id: roomId++ });
        rooms.push({ x: block.x + 1 + split, y: block.y + 1, width: innerW - split - 1, height: innerH, id: roomId++ });
      } else {
        const split = Math.floor(innerH / 2);
        rooms.push({ x: block.x + 1, y: block.y + 1, width: innerW, height: split - 1, id: roomId++ });
        rooms.push({ x: block.x + 1, y: block.y + 1 + split, width: innerW, height: innerH - split - 1, id: roomId++ });
      }
    } else {
      rooms.push({ x: block.x + 1, y: block.y + 1, width: innerW, height: innerH, id: roomId++ });
    }
  }

  // Filter tiny rooms, carve interiors, place doors
  const validRooms = rooms.filter(r => r.width >= 2 && r.height >= 2);
  for (const room of validRooms) {
    carveRoom(map, room.x, room.y, room.width, room.height);
  }

  for (const room of validRooms) {
    const doors = placeBuildingDoor(map, room, rng);
    buildings.push({ room, category: 'residence', doors, districtType: rect.type });
  }

  return { rooms: validRooms, buildings };
}

// ============================================================================
// MARKET DISTRICT — Central open plaza + stalls + surrounding buildings
// ============================================================================

export function generateMarket(
  map: GridMap, rect: DistrictRect, rng: SeededRandom, roomIdStart: number,
): DistrictResult {
  const rooms: Room[] = [];
  const buildings: CityBuilding[] = [];
  let roomId = roomIdStart;

  const cx = Math.floor(rect.x + rect.width / 2);
  const cy = Math.floor(rect.y + rect.height / 2);

  // Central plaza — 40% of the smaller dimension
  const plazaRadius = Math.max(3, Math.floor(Math.min(rect.width, rect.height) * 0.2));

  // Carve the plaza
  for (let dy = -plazaRadius; dy <= plazaRadius; dy++) {
    for (let dx = -plazaRadius; dx <= plazaRadius; dx++) {
      const px = cx + dx;
      const py = cy + dy;
      if (inBounds(map, px, py) && px >= rect.x && px < rect.x + rect.width &&
          py >= rect.y && py < rect.y + rect.height) {
        setTerrain(map, px, py, 'floor');
      }
    }
  }

  // Place fountain in center
  map.entities.push({
    id: `obj_market_fountain_${rect.id}`,
    x: cx, y: cy, type: 'object', name: 'Fountain',
  });

  // Radiating streets to edges (4 cardinal directions)
  const streetW = 1;
  // North
  for (let y = rect.y; y < cy - plazaRadius; y++) {
    for (let dx = 0; dx < streetW; dx++) {
      if (inBounds(map, cx + dx, y)) setTerrain(map, cx + dx, y, 'floor');
    }
  }
  // South
  for (let y = cy + plazaRadius + 1; y < rect.y + rect.height; y++) {
    for (let dx = 0; dx < streetW; dx++) {
      if (inBounds(map, cx + dx, y)) setTerrain(map, cx + dx, y, 'floor');
    }
  }
  // West
  for (let x = rect.x; x < cx - plazaRadius; x++) {
    for (let dy = 0; dy < streetW; dy++) {
      if (inBounds(map, x, cy + dy)) setTerrain(map, x, cy + dy, 'floor');
    }
  }
  // East
  for (let x = cx + plazaRadius + 1; x < rect.x + rect.width; x++) {
    for (let dy = 0; dy < streetW; dy++) {
      if (inBounds(map, x, cy + dy)) setTerrain(map, x, cy + dy, 'floor');
    }
  }

  // Place market stalls around plaza perimeter
  const stallPositions: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const sx = cx + Math.round(Math.cos(angle) * (plazaRadius - 1));
    const sy = cy + Math.round(Math.sin(angle) * (plazaRadius - 1));
    if (inBounds(map, sx, sy) && !(sx === cx && sy === cy)) {
      const cell = getCell(map, sx, sy);
      if (cell && cell.terrain === 'floor') {
        stallPositions.push({ x: sx, y: sy });
      }
    }
  }
  for (const pos of stallPositions) {
    if (rng.bool(0.6)) {
      map.entities.push({
        id: `obj_stall_${rect.id}_${pos.x}_${pos.y}`,
        x: pos.x, y: pos.y, type: 'object',
        name: rng.pick(['Market Stall', 'Fruit Cart', 'Fish Stand', 'Spice Cart']),
      });
    }
  }

  // Small buildings around the perimeter (in the 4 quadrants)
  const buildingSize = 3;
  const quadrants = [
    { x: rect.x + 1, y: rect.y + 1 },                                               // NW
    { x: rect.x + rect.width - buildingSize - 2, y: rect.y + 1 },                    // NE
    { x: rect.x + 1, y: rect.y + rect.height - buildingSize - 2 },                   // SW
    { x: rect.x + rect.width - buildingSize - 2, y: rect.y + rect.height - buildingSize - 2 }, // SE
  ];

  for (const q of quadrants) {
    // Place 1-2 small buildings per quadrant
    const count = rng.int(1, 2);
    for (let i = 0; i < count; i++) {
      const bx = q.x + i * (buildingSize + 2);
      const by = q.y;
      if (bx + buildingSize >= rect.x + rect.width - 1) continue;
      if (by + buildingSize >= rect.y + rect.height - 1) continue;

      const room: Room = { x: bx, y: by, width: buildingSize, height: buildingSize, id: roomId++ };
      carveRoom(map, room.x, room.y, room.width, room.height);
      rooms.push(room);

      const doors = placeBuildingDoor(map, room, rng);
      buildings.push({ room, category: 'residence', doors, districtType: rect.type });
    }
  }

  return { rooms, buildings };
}

// ============================================================================
// NOBLE DISTRICT — Wide streets, large buildings, courtyards
// ============================================================================

export function generateNoble(
  map: GridMap, rect: DistrictRect, rng: SeededRandom, roomIdStart: number,
): DistrictResult {
  const rooms: Room[] = [];
  const buildings: CityBuilding[] = [];
  let roomId = roomIdStart;

  const streetWidth = 2;
  const blockSize = 10 + rng.int(0, 3);

  const x0 = rect.x + 1;
  const y0 = rect.y + 1;
  const x1 = rect.x + rect.width - 1;
  const y1 = rect.y + rect.height - 1;

  // Wide streets
  for (let y = y0; y < y1; y += blockSize + streetWidth) {
    for (let dy = 0; dy < streetWidth; dy++) {
      const sy = y + dy;
      if (sy < y1) {
        for (let x = x0; x < x1; x++) setTerrain(map, x, sy, 'floor');
      }
    }
  }
  for (let x = x0; x < x1; x += blockSize + streetWidth) {
    for (let dx = 0; dx < streetWidth; dx++) {
      const sx = x + dx;
      if (sx < x1) {
        for (let y = y0; y < y1; y++) setTerrain(map, sx, y, 'floor');
      }
    }
  }

  // Identify blocks — each block becomes a single large building or estate
  const blocks = identifyWallBlocks(map, x0, y0, x1 - x0, y1 - y0);
  for (const block of blocks) {
    const innerW = block.width - 2;
    const innerH = block.height - 2;
    if (innerW < 3 || innerH < 3) continue;

    // 30% chance of courtyard: carve a border room with an open interior
    if (rng.bool(0.3) && innerW >= 6 && innerH >= 6) {
      // Outer room (L-shaped border)
      const room: Room = { x: block.x + 1, y: block.y + 1, width: innerW, height: innerH, id: roomId++ };
      carveRoom(map, room.x, room.y, room.width, room.height);
      rooms.push(room);

      // Inner courtyard (open to sky — just floor, we treat it as a garden)
      const courtX = block.x + 3;
      const courtY = block.y + 3;
      const courtW = Math.max(2, innerW - 4);
      const courtH = Math.max(2, innerH - 4);
      for (let cy = courtY; cy < courtY + courtH && cy < map.height; cy++) {
        for (let cx = courtX; cx < courtX + courtW && cx < map.width; cx++) {
          setTerrain(map, cx, cy, 'floor');
        }
      }
      // Courtyard walls to create the L-shape
      for (let cy = courtY; cy < courtY + courtH && cy < map.height; cy++) {
        for (let cx = courtX; cx < courtX + courtW && cx < map.width; cx++) {
          // Ring of walls around courtyard edge
          if (cx === courtX || cx === courtX + courtW - 1 ||
              cy === courtY || cy === courtY + courtH - 1) {
            setTerrain(map, cx, cy, 'wall');
          }
        }
      }
      // Opening from building into courtyard
      const doorX = courtX + Math.floor(courtW / 2);
      const doorY = courtY;
      if (inBounds(map, doorX, doorY)) setTerrain(map, doorX, doorY, 'door');

      // Place tree/statue in courtyard center
      const ccx = courtX + Math.floor(courtW / 2);
      const ccy = courtY + Math.floor(courtH / 2);
      if (inBounds(map, ccx, ccy)) {
        const cell = getCell(map, ccx, ccy);
        if (cell && cell.terrain === 'floor') {
          map.entities.push({
            id: `obj_garden_${room.id}`,
            x: ccx, y: ccy, type: 'object',
            name: rng.pick(['Statue', 'Tree', 'Garden Fountain']),
          });
        }
      }

      const doors = placeBuildingDoor(map, room, rng);
      buildings.push({ room, category: 'residence', doors, districtType: rect.type });
    } else {
      // Single large building
      const room: Room = { x: block.x + 1, y: block.y + 1, width: innerW, height: innerH, id: roomId++ };
      carveRoom(map, room.x, room.y, room.width, room.height);
      rooms.push(room);
      const doors = placeBuildingDoor(map, room, rng);
      buildings.push({ room, category: 'residence', doors, districtType: rect.type });
    }
  }

  return { rooms, buildings };
}

// ============================================================================
// SLUMS DISTRICT — Cellular automata organic alleys
// ============================================================================

export function generateSlums(
  map: GridMap, rect: DistrictRect, rng: SeededRandom, roomIdStart: number,
): DistrictResult {
  const rooms: Room[] = [];
  const buildings: CityBuilding[] = [];
  let roomId = roomIdStart;

  const x0 = rect.x;
  const y0 = rect.y;
  const w = rect.width;
  const h = rect.height;

  // Step 1: Random fill within district bounds (higher fill = more walls = narrower alleys)
  const fillProb = 0.52;
  for (let y = y0 + 1; y < y0 + h - 1; y++) {
    for (let x = x0 + 1; x < x0 + w - 1; x++) {
      if (inBounds(map, x, y)) {
        setTerrain(map, x, y, rng.bool(1 - fillProb) ? 'floor' : 'wall');
      }
    }
  }

  // Step 2: Cellular automata smoothing (4-5 rule)
  for (let i = 0; i < 4; i++) {
    const snapshot: Array<Array<string>> = [];
    for (let y = y0; y < y0 + h; y++) {
      const row: string[] = [];
      for (let x = x0; x < x0 + w; x++) {
        const cell = getCell(map, x, y);
        row.push(cell ? cell.terrain : 'wall');
      }
      snapshot.push(row);
    }

    for (let y = y0 + 1; y < y0 + h - 1; y++) {
      for (let x = x0 + 1; x < x0 + w - 1; x++) {
        let wallCount = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const ly = y - y0 + dy;
            const lx = x - x0 + dx;
            if (ly < 0 || ly >= h || lx < 0 || lx >= w) {
              wallCount++;
            } else if (snapshot[ly]?.[lx] === 'wall') {
              wallCount++;
            }
          }
        }
        setTerrain(map, x, y, wallCount >= 5 ? 'wall' : 'floor');
      }
    }
  }

  // Step 3: Flood-fill to find largest connected region and keep only it
  const visited = new Set<string>();
  let largestRegion: string[] = [];

  for (let y = y0 + 1; y < y0 + h - 1; y++) {
    for (let x = x0 + 1; x < x0 + w - 1; x++) {
      const key = coordKey(x, y);
      const cell = getCell(map, x, y);
      if (visited.has(key) || !cell || cell.terrain !== 'floor') continue;

      const region: string[] = [];
      const stack = [{ x, y }];
      while (stack.length > 0) {
        const { x: cx, y: cy } = stack.pop()!;
        const k = coordKey(cx, cy);
        if (visited.has(k)) continue;
        if (cx < x0 || cx >= x0 + w || cy < y0 || cy >= y0 + h) continue;
        const c = getCell(map, cx, cy);
        if (!c || c.terrain !== 'floor') continue;
        visited.add(k);
        region.push(k);
        stack.push({ x: cx + 1, y: cy }, { x: cx - 1, y: cy }, { x: cx, y: cy + 1 }, { x: cx, y: cy - 1 });
      }

      if (region.length > largestRegion.length) largestRegion = region;
    }
  }

  const largestSet = new Set(largestRegion);
  for (let y = y0 + 1; y < y0 + h - 1; y++) {
    for (let x = x0 + 1; x < x0 + w - 1; x++) {
      const cell = getCell(map, x, y);
      if (cell && cell.terrain === 'floor' && !largestSet.has(coordKey(x, y))) {
        setTerrain(map, x, y, 'wall');
      }
    }
  }

  // Step 4: Scan for wall pockets (small enclosed wall rectangles) as "buildings"
  const usedWalls = new Set<string>();
  for (let y = y0 + 1; y < y0 + h - 2; y++) {
    for (let x = x0 + 1; x < x0 + w - 2; x++) {
      const cell = getCell(map, x, y);
      if (!cell || cell.terrain !== 'wall' || usedWalls.has(coordKey(x, y))) continue;

      // Try to find a small rectangular wall pocket
      let pw = 0;
      while (x + pw < x0 + w - 1 && pw < 5) {
        const c = getCell(map, x + pw, y);
        if (!c || c.terrain !== 'wall' || usedWalls.has(coordKey(x + pw, y))) break;
        pw++;
      }
      if (pw < 3) continue;

      let ph = 0;
      outer: while (y + ph < y0 + h - 1 && ph < 5) {
        for (let dx = 0; dx < pw; dx++) {
          const c = getCell(map, x + dx, y + ph);
          if (!c || c.terrain !== 'wall' || usedWalls.has(coordKey(x + dx, y + ph))) break outer;
        }
        ph++;
      }
      if (ph < 3) continue;

      // Mark as used
      for (let dy = 0; dy < ph; dy++) {
        for (let dx = 0; dx < pw; dx++) {
          usedWalls.add(coordKey(x + dx, y + dy));
        }
      }

      // Carve interior (leave 1-tile wall border)
      const room: Room = { x: x + 1, y: y + 1, width: pw - 2, height: ph - 2, id: roomId++ };
      if (room.width >= 1 && room.height >= 1) {
        carveRoom(map, room.x, room.y, room.width, room.height);
        rooms.push(room);
        const doors = placeBuildingDoor(map, room, rng);
        buildings.push({ room, category: 'residence', doors, districtType: rect.type });
      }
    }
  }

  return { rooms, buildings };
}

// ============================================================================
// BOUNDARY STITCHING
// ============================================================================

/** Walk shared boundaries between districts and ensure floor tiles connect. */
export function stitchBoundaries(map: GridMap, districts: DistrictRect[], avenueWidth: number): void {
  for (let i = 0; i < districts.length; i++) {
    for (let j = i + 1; j < districts.length; j++) {
      const a = districts[i];
      const b = districts[j];

      // Check if they share a horizontal boundary
      if (a.y + a.height === b.y || b.y + b.height === a.y) {
        const boundaryY = a.y + a.height === b.y ? a.y + a.height : b.y + b.height;
        const overlapX0 = Math.max(a.x, b.x);
        const overlapX1 = Math.min(a.x + a.width, b.x + b.width);

        // Ensure at least some floor tiles along the boundary
        const midX = Math.floor((overlapX0 + overlapX1) / 2);
        for (let dx = -avenueWidth; dx <= avenueWidth; dx++) {
          const x = midX + dx;
          for (let dy = -1; dy <= 1; dy++) {
            const y = boundaryY + dy;
            if (inBounds(map, x, y)) setTerrain(map, x, y, 'floor');
          }
        }
      }

      // Check if they share a vertical boundary
      if (a.x + a.width === b.x || b.x + b.width === a.x) {
        const boundaryX = a.x + a.width === b.x ? a.x + a.width : b.x + b.width;
        const overlapY0 = Math.max(a.y, b.y);
        const overlapY1 = Math.min(a.y + a.height, b.y + b.height);

        const midY = Math.floor((overlapY0 + overlapY1) / 2);
        for (let dy = -avenueWidth; dy <= avenueWidth; dy++) {
          const y = midY + dy;
          for (let dx = -1; dx <= 1; dx++) {
            const x = boundaryX + dx;
            if (inBounds(map, x, y)) setTerrain(map, x, y, 'floor');
          }
        }
      }
    }
  }
}

// ============================================================================
// SHARED UTILITIES
// ============================================================================

interface WallBlock {
  x: number; y: number; width: number; height: number;
}

function identifyWallBlocks(map: GridMap, x0: number, y0: number, w: number, h: number): WallBlock[] {
  const visited = Array.from({ length: h }, () => new Array<boolean>(w).fill(false));
  const blocks: WallBlock[] = [];

  for (let ly = 0; ly < h; ly++) {
    for (let lx = 0; lx < w; lx++) {
      if (visited[ly][lx]) continue;
      const gx = x0 + lx;
      const gy = y0 + ly;
      const cell = getCell(map, gx, gy);
      if (!cell || cell.terrain !== 'wall') {
        visited[ly][lx] = true;
        continue;
      }

      let bw = 0;
      while (lx + bw < w) {
        const c = getCell(map, x0 + lx + bw, gy);
        if (!c || c.terrain !== 'wall' || visited[ly][lx + bw]) break;
        bw++;
      }

      let bh = 1;
      outer: while (ly + bh < h) {
        for (let dx = 0; dx < bw; dx++) {
          const c = getCell(map, x0 + lx + dx, y0 + ly + bh);
          if (!c || c.terrain !== 'wall' || visited[ly + bh][lx + dx]) break outer;
        }
        bh++;
      }

      for (let dy = 0; dy < bh; dy++) {
        for (let dx = 0; dx < bw; dx++) {
          visited[ly + dy][lx + dx] = true;
        }
      }

      if (bw >= 4 && bh >= 4) {
        blocks.push({ x: x0 + lx, y: y0 + ly, width: bw, height: bh });
      }
    }
  }

  return blocks;
}

function placeBuildingDoor(
  map: GridMap, room: Room, rng: SeededRandom,
): Array<{ x: number; y: number }> {
  const doors: Array<{ x: number; y: number }> = [];
  const candidates: Array<{ x: number; y: number; side: string }> = [];

  // North edge
  for (let x = room.x; x < room.x + room.width; x++) {
    const wy = room.y - 1;
    if (isStreetAdjacentDir(map, x, wy, 'N')) candidates.push({ x, y: wy, side: 'N' });
  }
  // South edge
  for (let x = room.x; x < room.x + room.width; x++) {
    const wy = room.y + room.height;
    if (isStreetAdjacentDir(map, x, wy, 'S')) candidates.push({ x, y: wy, side: 'S' });
  }
  // West edge
  for (let y = room.y; y < room.y + room.height; y++) {
    const wx = room.x - 1;
    if (isStreetAdjacentDir(map, wx, y, 'W')) candidates.push({ x: wx, y, side: 'W' });
  }
  // East edge
  for (let y = room.y; y < room.y + room.height; y++) {
    const wx = room.x + room.width;
    if (isStreetAdjacentDir(map, wx, y, 'E')) candidates.push({ x: wx, y, side: 'E' });
  }

  if (candidates.length > 0) {
    const first = rng.pick(candidates);
    setTerrain(map, first.x, first.y, 'door');
    doors.push({ x: first.x, y: first.y });

    const otherSide = candidates.filter(c => c.side !== first.side);
    if (otherSide.length > 0 && rng.bool(0.25)) {
      const second = rng.pick(otherSide);
      setTerrain(map, second.x, second.y, 'door');
      doors.push({ x: second.x, y: second.y });
    }
  }

  return doors;
}

function isStreetAdjacentDir(map: GridMap, x: number, y: number, side: string): boolean {
  const cell = getCell(map, x, y);
  if (!cell || cell.terrain !== 'wall') return false;

  let sx = x, sy = y;
  if (side === 'N') sy = y - 1;
  if (side === 'S') sy = y + 1;
  if (side === 'W') sx = x - 1;
  if (side === 'E') sx = x + 1;

  const streetCell = getCell(map, sx, sy);
  return streetCell !== null && streetCell.terrain === 'floor';
}
